/**
 * Theming props for the Saved Opportunities Widget.
 * These map to CSS custom properties used internally.
 */
export interface SavedOpportunitiesThemingProps {
  /** Primary color */
  savedPrimaryColor?: string;
  /** Accent color */
  savedAccentColor?: string;
  /** Container background color */
  savedBackgroundColor?: string;
  /** Card background */
  savedCardBg?: string;
  /** Card hover background */
  savedCardHoverBg?: string;
  /** Primary text color */
  savedTextColor?: string;
  /** Secondary text color */
  savedSecondaryTextColor?: string;
  /** Heading color */
  savedHeadingColor?: string;
  /** Border color */
  savedBorderColor?: string;
  /** Card border color */
  savedCardBorder?: string;
  /** Container border radius */
  savedBorderRadius?: string;
  /** Card border radius */
  savedCardRadius?: string;
  /** Button border radius */
  savedButtonRadius?: string;
  /** Container box shadow */
  savedShadow?: string;
  /** Card box shadow */
  savedCardShadow?: string;
  /** Container padding */
  savedPadding?: string;
  /** Card padding */
  savedCardPadding?: string;
  /** Gap between cards */
  savedGap?: string;
  /** Font family */
  savedFontFamily?: string;
  /** Title font size */
  savedTitleSize?: string;
  /** Body font size */
  savedBodySize?: string;
  /** Meta font size */
  savedMetaSize?: string;
  /** Title font weight */
  savedTitleWeight?: string;
  /** Button background */
  savedButtonBg?: string;
  /** Button text color */
  savedButtonColor?: string;
  /** Button hover background */
  savedButtonHoverBg?: string;
  /** Empty state icon color */
  savedEmptyIconColor?: string;
  /** Empty state text color */
  savedEmptyTextColor?: string;
  /** Transition duration */
  savedTransitionDuration?: string;
  /** Hover scale factor */
  savedHoverScale?: string;
}

/**
 * Props for the LegionSavedOpportunities React component.
 */
export interface SavedOpportunitiesWidgetProps
  extends SavedOpportunitiesThemingProps {
  /** Base URL of your Legion deployment (required). */
  baseUrl: string;
  /** JWT / API key for authenticated access (sent via postMessage, never in URL). */
  authToken?: string;
  /** Theme preset. */
  theme?: "light" | "dark";
  /** Widget width (CSS value). */
  width?: string;
  /** Widget height (CSS value). */
  height?: string;
  /** CSS class applied to the outer container. */
  className?: string;
  /** Inline styles merged into the container. */
  style?: React.CSSProperties;
  /** Called when the widget iframe finishes loading. */
  onLoad?: (detail: { widget: string; theme: string }) => void;
  /** Called if the widget iframe fails to load. */
  onError?: (message: string) => void;
}

/**
 * Maps camelCase theming prop names to their kebab-case CSS variable query param names.
 */
export const SAVED_THEMING_PROP_TO_PARAM: Record<
  keyof SavedOpportunitiesThemingProps,
  string
> = {
  savedPrimaryColor: "saved-primary-color",
  savedAccentColor: "saved-accent-color",
  savedBackgroundColor: "saved-background-color",
  savedCardBg: "saved-card-bg",
  savedCardHoverBg: "saved-card-hover-bg",
  savedTextColor: "saved-text-color",
  savedSecondaryTextColor: "saved-secondary-text-color",
  savedHeadingColor: "saved-heading-color",
  savedBorderColor: "saved-border-color",
  savedCardBorder: "saved-card-border",
  savedBorderRadius: "saved-border-radius",
  savedCardRadius: "saved-card-radius",
  savedButtonRadius: "saved-button-radius",
  savedShadow: "saved-shadow",
  savedCardShadow: "saved-card-shadow",
  savedPadding: "saved-padding",
  savedCardPadding: "saved-card-padding",
  savedGap: "saved-gap",
  savedFontFamily: "saved-font-family",
  savedTitleSize: "saved-title-size",
  savedBodySize: "saved-body-size",
  savedMetaSize: "saved-meta-size",
  savedTitleWeight: "saved-title-weight",
  savedButtonBg: "saved-button-bg",
  savedButtonColor: "saved-button-color",
  savedButtonHoverBg: "saved-button-hover-bg",
  savedEmptyIconColor: "saved-empty-icon-color",
  savedEmptyTextColor: "saved-empty-text-color",
  savedTransitionDuration: "saved-transition-duration",
  savedHoverScale: "saved-hover-scale",
};

